import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AccountService } from './account.service';
import { PaymentIntentResult, StripeService } from 'ngx-stripe';
import STATES from '../../../../old-crazy-web-frontend/src/app/states';
import { NotificationService } from '../notification.service';
import { ConfigService } from '../../config/config.service';
import { AlertPaymentService } from './alert-payment.service';
import { PaymentParams } from '../../account/account.model';
import { SubscriptionCheckoutService } from '../../account/subscription-checkout.service';

@Injectable()
export class PaymentService {
  public spinnerActivate = new BehaviorSubject<boolean>(opener);
  private $state;

  constructor(
    private http: HttpClient,
    public accountService: AccountService,
    private router: Router,
    public stripeService: StripeService,
    public notificationService: NotificationService,
    private configService: ConfigService,
    public alertPaymentService: AlertPaymentService,
    private checkoutService: SubscriptionCheckoutService
  ) {
    this.$state = this.configService.getValue('$state');
  }

  checkCurrentSubscriptionPaymentStatus(redirectState) {
    this.checkPaymentStatus(() => this.accountService.getCurrentSubscriptionPaymentStatus(), redirectState);
  }

  payNonDirectPayment() {
    this.http.post(`/finance/subscription/payment/url`, null).subscribe((redirect: any) => {
      window.location.href = redirect.confirmationRedirectUrl;
    });
  }

  monitorBillingPayment(chargeId: string): Observable<any> {
    return this.http.post(`/finance/billing/monitor`, null, { params: { chargeId: chargeId } });
  }

  monitorCheckoutPayment(): Observable<any> {
    return this.http.get(`/finance/checkout/monitor`);
  }

  checkPaymentStatus(paymentStatusFcn, redirectState) {
    this.onSpinnerActivate(true);
    paymentStatusFcn().subscribe((payment: any) => {
      if (payment.status === 'requires_action') {
        console.log('Plan Payment requires action');

        if (payment.directPayment) {
          this.stripeService.handleCardPayment(payment.clientSecret, {}).subscribe((result: PaymentIntentResult) => {
            if (result.error) {
              this.notificationService.open(result.error.message, 'PLANS.CHARGE_ADD_BALANCE', { closable: true });
              this.redirect(redirectState);
            } else {
              this.notificationService.open('RECHARGE_SUCCESS');
              this.redirect(redirectState);
            }
            this.onSpinnerActivate(false);
            this.alertPaymentService.alertUnpaidStatus();
          });
        } else {
          this.notificationService.open('PLANS.CHARGE_NON_DIRECT_PAYMENT', {}, { closable: true });
        }
      } else if ('processing' === payment.status) {
        console.log('Plan Payment processing');
        setTimeout(() => {
          this.checkPaymentStatus(paymentStatusFcn, redirectState);
        }, 1000);
      } else if (['requires_payment_method', 'canceled', 'requires_confirmation'].includes(payment.status)) {
        if (payment.directPayment) {
          console.log(`Plan Payment failed: ${payment.status}`);
          this.notificationService.open(`PLANS.${payment.status.toUpperCase()}`, 'PLANS.CHARGE_ADD_BALANCE');
          this.redirect(redirectState);
          this.onSpinnerActivate(false);
          this.alertPaymentService.activatePaymentAlert(true);
        } else {
          this.notificationService.open('PLANS.CHARGE_NON_DIRECT_PAYMENT', {}, { closable: true });
        }
      } else if ('succeeded' === payment.status || payment.amount === 0) {
        console.log('Plan Payment succeeded');
        this.notificationService.open('RECHARGE_SUCCESS');
        this.redirect(redirectState);
        this.onSpinnerActivate(false);
        this.alertPaymentService.activatePaymentAlert(false);
        this.alertPaymentService.alertUnpaidStatus();
      } else {
        this.notificationService.open('PLANS.CHARGE_ADD_BALANCE_ERROR');
        this.redirect(redirectState);
        this.onSpinnerActivate(false);
        this.alertPaymentService.alertUnpaidStatus();
      }
    });
  }

  redirect(redirectState) {
    if (redirectState === '/users' || redirectState === STATES.USERS_DETAILS || redirectState === STATES.USERS) {
      this.router.navigate(['/users/all'], { queryParams: { changedPlan: true, newInvitation: true } });
    } else if (redirectState === 'my-account') {
      setTimeout(() => {
        window.location.href = '/my-account';
      }, 500);
    } else if (redirectState.startsWith('phone-numbers-')) {
      const selectedMsisdnDisplayedValue = redirectState.replace('phone-numbers-', '');
      const queryParams = { isNew: true } as any;
      if (!!selectedMsisdnDisplayedValue) {
        queryParams.selectedNumber = selectedMsisdnDisplayedValue;
      }
      setTimeout(() => {
        this.router.navigate(['phone-numbers'], { queryParams });
      }, 1000);
    } else {
      this.router.navigateByUrl('/my-account');
    }
  }

  onSpinnerActivate(active: boolean) {
    this.spinnerActivate.next(active);
  }

  handleSubscriptionPayment(v: PaymentParams, originState: string) {
    if (v.checkoutSessionId) {
      this.checkoutService.redirectToCheckout(v.checkoutSessionId);
    } else if (v.confirmationRedirectUrl) {
      window.location.href = v.confirmationRedirectUrl;
    } else {
      this.checkCurrentSubscriptionPaymentStatus(originState);
    }
  }
}
